import { useCustomQuery } from "@/hooks/useCustomQuery"
import DetailAvatar from "@/components/Avatar/DetailAvatar"
import LetterAvatar from "@/components/Avatar/LetterAvatar"
import { DetailTable } from "@/components/Tables/DetailTable"
import { columns } from "@/pages/orders/DetailColumns"
import { CrudProvider, useCrudContext } from "@/context/crudContext"

import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Divider,
} from "@mui/material"
import { useParams, Link } from "react-router-dom"
import { useState, useEffect } from "react"
import Loading from "@/components/Loading"
import { formatToDevanagariStyle } from "@/utilities/helper"
import StarIcon from "@mui/icons-material/Star"

const ProductDetails = () => {
  const { id } = useParams()

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useCustomQuery(["product-detail", id], `product/${id}`)

  if (isLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  const product = response?.data

  return (
    <div>
      {/* Breadcrumb */}
      <div className="breadcrumb-card">
        <h5>Product Details</h5>
        <ul className="breadcrumb text-sm">
          <li>
            <Link to="//">
              <i className="material-symbols-outlined">home</i> Dashboard
            </Link>
          </li>
          <li>
            <Link to="/products">Products</Link>
          </li>
          <li>{product.code}</li>
        </ul>
      </div>

      {product ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 xxl:grid-cols-5 gap-6">
          {/* Left Panel - Product Info */}
          <div className="bg-white border-0 rounded-3 p-4 mb-4">
            <div className="flex items-center mb-6">
              {product.thumbnail ? (
                <img
                  src={product.thumbnail.url}
                  alt={product.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <LetterAvatar name={product?.name} />
              )}
            </div>

            <DetailItem label="Code" value={product.code} />
            <DetailItem label="SKU" value={product.sku} />
            {product.brand_name && (
              <DetailItem label="Brand" value={product.brand_name} />
            )}
            <div className="mb-3">
              <span className="block text-sm text-gray-600 mb-1">Tags:</span>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(product.tags)
                  ? product.tags.map((tag, index) => (
                      <TagsBadge key={tag.id || index} name={tag?.name} />
                    ))
                  : product.tags?.name && (
                      <TagsBadge name={product.tags.name} />
                    )}
              </div>
            </div>
            <DetailItem label="Item Stock" value={product.stock} />
            <DetailItem
              label="Price"
              value={`Rs ${formatToDevanagariStyle(product.sell_price)}`}
            />
            <DetailItem label="Supplier" value={product.supplier?.name} />
            <DetailItem label="Updated At" value={product.updated_at} />
            <DetailItem label="Created At" value={product.created_at} />
            <DetailItem label="Created By" value={product.createdBy?.name} />
            <DetailItem label="Updated By" value={product.updatedBy?.name} />
          </div>

          {/* Right Panel - Images and Orders */}
          <div className="lg:col-span-3 xxl:col-span-4 space-y-6">
            {/* Product Images */}
            {product.images && product.images.length > 0 && (
              <div className="card bg-white border-0 px-4 py-5 rounded-3">
                <p className="mb-5 text-base font-semibold text-black">
                  Product Images
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {product.images.map((image) => (
                    <div
                      key={image.id}
                      className={`relative border-2 rounded-lg overflow-hidden aspect-square ${
                        image.is_thumbnail
                          ? "border-blue-500"
                          : "border-gray-200"
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.original_name || product.name}
                        className="w-full h-full object-cover"
                      />
                      {image.is_thumbnail && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                          <StarIcon sx={{ fontSize: 16 }} />
                        </div>
                      )}
                      {image.is_thumbnail && (
                        <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-center py-1 text-xs">
                          Thumbnail
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Orders */}
            <div className="card bg-white border-0 px-4 py-5 rounded-3">
              <p className="mb-5 text-base font-semibold text-black">
                Recent Orders
              </p>
              <CrudProvider>
                <OrderTable product={product} isLoading={isLoading} />
              </CrudProvider>
            </div>
          </div>
        </div>
      ) : (
        <div>No product data found.</div>
      )}
    </div>
  )
}

function OrderTable({ product, isLoading }) {
  return (
    <DetailTable
      label="Orders"
      columns={columns}
      data={product.orders}
      isLoading={isLoading}
    />
  )
}

function TagsBadge({ name }) {
  return (
    <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-medium capitalize">
      {name}
    </span>
  )
}

function DetailItem({ label, value }) {
  return (
    <div className="mb-3">
      <p className="text-sm text-gray-600">{label}:</p>
      <span className="text-sm font-medium text-gray-900 break-words">
        {value}
      </span>
    </div>
  )
}

export default ProductDetails
